import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Users, Circle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Global header "People Online" indicator for team members. Candidate rows only for Support-enabled staff.
export function SupportPresenceIndicator() {
  const [allowed, setAllowed] = useState(true);
  const [visible, setVisible] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const navigate = useNavigate();
  const timer = useRef();

  useEffect(() => { api.get("/support/presence/me").then((r) => setVisible(r.data.visible)).catch(() => {}); }, []);

  const load = useCallback(() => {
    api.get("/support/online-people").then((r) => setData(r.data)).catch((e) => { if (e?.response?.status === 403) setAllowed(false); setData(null); });
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    load();
    timer.current = setInterval(load, 40000);
    const onFocus = () => load();
    const onCtx = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("tr-district-change", onCtx);
    window.addEventListener("tr-account-change", onCtx);
    return () => { clearInterval(timer.current); window.removeEventListener("focus", onFocus); window.removeEventListener("tr-district-change", onCtx); window.removeEventListener("tr-account-change", onCtx); };
  }, [allowed, load]);

  const toggleVisible = async (v) => {
    setVisible(v);
    try { await api.put("/support/presence/me", { visible: v }); toast.success(v ? "Others can now see you're online" : "You now appear offline to others"); load(); }
    catch (e) { setVisible(!v); toast.error("Could not update your online status"); }
  };

  if (!allowed) return null;
  const total = data?.total || 0;
  const people = (data?.people || []).filter((p) => !filter || p.classifications.includes(filter));

  const startChat = async (p) => {
    if (!p.chat_eligible) return;
    try { const { data: d } = await api.post("/support/conversations/start-with-candidate", { person_id: p.person_id });
      navigate(`/communications?tab=support&conv=${d.conversation_id}`); }
    catch (e) { toast.error("Could not start chat"); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="people-online-indicator" className="flex items-center gap-1.5 rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm hover:bg-secondary/60 text-slate-600">
          <Users className="w-[18px] h-[18px]" />
          <span className="font-medium">{total}</span>
          <span className="hidden 2xl:inline text-slate-500">Online</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="bg-white w-80 p-0" data-testid="people-online-popover">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-800">People Online — {total}</p>
          <label className="mt-2 flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
            <span>Show me as online</span>
            <Switch checked={visible} onCheckedChange={toggleVisible} data-testid="presence-visible-switch" />
          </label>
          {data?.category_overlapping && <p className="text-[10px] text-slate-400">Category counts may overlap (a person can hold multiple classifications).</p>}
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <button onClick={() => setFilter(null)} data-testid="cat-all" className={`text-[11px] rounded px-1.5 py-1 border ${!filter ? "border-brand text-brand bg-brand/5" : "border-slate-200 text-slate-500"}`}>All {total}</button>
            {(data?.categories || []).filter((c) => c.count || c.key === "team" || c.key === "candidate").map((c) => (
              <button key={c.key} onClick={() => setFilter(filter === c.key ? null : c.key)} data-testid={`cat-${c.key}`}
                className={`text-[11px] rounded px-1.5 py-1 border flex justify-between ${filter === c.key ? "border-brand text-brand bg-brand/5" : "border-slate-200 text-slate-500"}`}>
                <span className="truncate">{c.label}</span><span className="font-semibold ml-1">{c.count}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-72 overflow-auto divide-y divide-slate-50">
          {people.length === 0 && <p className="px-4 py-6 text-center text-xs text-slate-400" data-testid="people-online-empty">No one online in this view.</p>}
          {people.map((p) => (
            <div key={p.person_id} className="px-4 py-2.5 flex items-center gap-2.5" data-testid={`online-person-${p.person_id}`}>
              <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{p.person_name}{p.is_self && <span className="text-slate-400 font-normal"> (you)</span>}</p>
                <p className="text-[11px] text-slate-400 truncate">{p.classifications.map((c) => (c === "team" ? "Team member" : c.replace(/_/g, " "))).join(", ")}{p.organization_name ? ` · ${p.organization_name}` : ""}</p>
              </div>
              {p.chat_eligible && <button onClick={() => startChat(p)} data-testid={`online-start-${p.person_id}`} className="text-[11px] text-brand font-medium hover:underline shrink-0">Start Chat</button>}
            </div>
          ))}
        </div>
        <button onClick={() => navigate("/communications?tab=support")} data-testid="people-online-view-all" className="w-full text-center text-xs text-brand font-medium py-2.5 border-t border-slate-100 hover:bg-slate-50">View All Online →</button>
      </PopoverContent>
    </Popover>
  );
}
